import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  X,
  Plus,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Search,
} from 'lucide-react';
import { SkeletonTable } from '../components/Skeleton';
import { FilterSearchInput } from '../components/FormComponents';
import SortableHeader from '../components/SortableHeader';
import { fmtMoney, relativeTime } from '../utils/format';
import VendorMonogram from '../components/VendorMonogram';
import {
  RequestStatusBadge,
  ReviewStatusBadge,
  RiskRatingBadge,
  REQUEST_STATUS_OPTIONS as STATUS_OPTIONS,
  RISK_FILTER_OPTIONS as RISK_OPTIONS,
} from '../components/RequestBadges';
import { apiDownload, apiRequest } from '../services/apiClient';
import { normalizeSearchValue, readOptionParam, readSearchParam } from '../utils/urlFilters';
import useUrlSyncedFilters, { useReseedPageGuard } from '../hooks/useUrlSyncedFilters';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import RequestsBulkActionsBar from '../features/requests/RequestsBulkActionsBar';
import SavedViewsMenu from '../features/savedViews/SavedViewsMenu';
import { SAVED_VIEW_KEYS } from '../features/savedViews/savedViews';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// The ONE definition of this list's filters given a querystring — shared by the
// mount seeding and the external-change re-seed (doc-195) so the two paths
// cannot drift apart on doc-194's option validation or doc-168's trimmed q.
function seedFromParams(params) {
  return {
    status: readOptionParam(params, 'status', STATUS_OPTIONS),
    risk: readOptionParam(params, 'risk', RISK_OPTIONS),
    search: readSearchParam(params, 'q'),
    archived: params.get('archived') === 'true',
    page: parseInt(params.get('page')) || 1,
  };
}

// Identity of "what the page-reset effect is watching", for the re-seed guard.
// It must span EXACTLY that effect's dependency list — filters AND sort — or a
// change the effect reacts to produces no key change and gets swallowed
// (doc-224). Sort is not URL-synced here, so seedFromParams does not carry it
// and the re-seed call passes the live values alongside `next`.
function filtersKeyOf({ status, risk, search, archived, sortField, sortOrder }) {
  return JSON.stringify([status, risk, search, archived, sortField, sortOrder]);
}

export default function RequestsList() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Seeded from the URL, but only with values these selects can actually
  // display (doc-194) — an unknown status/risk would otherwise filter the list
  // while both dropdowns still read "All".
  const [seed] = useState(() => seedFromParams(searchParams));
  const [status, setStatus] = useState(seed.status);
  const [risk, setRisk] = useState(seed.risk);
  const [search, setSearch] = useState(seed.search);
  const [archived, setArchived] = useState(seed.archived);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem('requestsPageSize');
    return stored ? parseInt(stored) : 25;
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  // Bumped by the error callout's Retry button — and by the bulk bar after an
  // apply — so the fetch effect re-runs with identical filters. Without it a
  // failed load forces a hard refresh, and a bulk archive leaves stale rows.
  const [retryToken, setRetryToken] = useState(0);
  const refresh = useCallback(() => setRetryToken((token) => token + 1), []);

  // Bulk-select over the current page (admin only — the bulk endpoints are
  // admin-gated too). Cleared whenever the list refetches, in the fetch effect
  // below: a selection made against one result set must not silently carry
  // over to another.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedIds((prev) => (prev.size ? new Set() : prev));
  }, []);

  // Reset to page 1 when a filter changes — but not on mount, or a
  // deep-linked ?page=3 gets clobbered before the first fetch; and not when the
  // change is a re-seed landing, which carries its own page.
  const { markReseeded, isReseedArrival } = useReseedPageGuard();
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    if (isReseedArrival(filtersKeyOf({ status, risk, search, archived, sortField, sortOrder }))) return;
    setPage(1);
  }, [status, risk, search, archived, sortField, sortOrder, isReseedArrival]);

  // Re-seed when the querystring changes from OUTSIDE this component — the
  // sidebar "TPRM" link while already on /tprm, a CommandK jump to this list.
  // Without it the URL blanks and every control keeps its old value (doc-195).
  const markUrlWritten = useUrlSyncedFilters({
    searchParams,
    onExternalChange: useCallback((params) => {
      const next = seedFromParams(params);
      // Before the state updates, so the page-reset effect recognises the
      // filter change they cause and leaves next.page alone.
      // A re-seed does not touch sort, so the key it records carries the sort
      // values that will still be live when the effect runs.
      markReseeded(filtersKeyOf({ ...next, sortField, sortOrder }));
      setStatus(next.status);
      setRisk(next.risk);
      setSearch(next.search);
      setArchived(next.archived);
      setPage(next.page);
    }, [markReseeded, sortField, sortOrder]),
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (status) params.set('status', status); else params.delete('status');
    if (risk) params.set('risk', risk); else params.delete('risk');
    // The TRIMMED term, matching what the fetch below sends.
    const q = normalizeSearchValue(search);
    if (q) params.set('q', q); else params.delete('q');
    if (archived) params.set('archived', 'true'); else params.delete('archived');
    if (page > 1) params.set('page', page.toString()); else params.delete('page');
    setSearchParams(params, { replace: true });
    // Record what WE wrote so the re-seed above ignores our own write.
    markUrlWritten(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, risk, search, archived, page]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (risk) params.set('risk', risk);
    if (search.trim()) params.set('q', search.trim());
    if (archived) params.set('archived', 'true');
    params.set('sort', sortField);
    params.set('order', sortOrder);
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    // Debounced (search fires per keystroke) + stale-response guarded — a
    // slower earlier response must not paint over the latest, and a transient
    // failure must not leave the error panel up after a later success.
    let cancelled = false;
    setLoading(true);
    // Every refetch — filter, sort, page, page size, retry, post-bulk refresh —
    // drops the selection, so the bulk bar can never act on ids the user is no
    // longer looking at.
    clearSelection();
    const timer = setTimeout(() => {
      apiRequest(`/requests?${params.toString()}`)
        .then((data) => {
          if (cancelled) return;
          setRequests(data.requests || []);
          setPagination(data.pagination || { total: 0, totalPages: 1 });
          // ?page= comes from the URL so it can be out of range, and the server
          // CLAMPS rather than 404s (request-crud.js), reporting the page it
          // actually served. Adopt it, or a stale shared ?page=9 renders
          // "9 / 1" and "201–7 of 7" over the last page's rows (doc-183). The
          // URL-sync effect above then self-heals the querystring.
          const servedPage = data.pagination?.page;
          if (servedPage && servedPage !== page) setPage(servedPage);
          setError(null);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.message);
          setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status, risk, search, archived, sortField, sortOrder, page, pageSize, retryToken, clearSelection]);

  // Header checkbox: select every row on the current page, or clear if they
  // all already are. Page-scoped by design — no cross-page "select all N".
  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const pageIds = requests.map((r) => r.id);
      const allSelected = pageIds.length > 0 && pageIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(pageIds);
    });
  }, [requests]);

  const selectable = isAdmin;
  const allOnPageSelected = selectable && requests.length > 0 &&
    requests.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = selectable && requests.some((r) => selectedIds.has(r.id));

  // "Some but not all" renders as indeterminate — only settable via the DOM.
  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  const handlePageSizeChange = (n) => {
    setPageSize(n);
    localStorage.setItem('requestsPageSize', n.toString());
    setPage(1);
  };

  // Filter-aware CSV export (doc-116): same params as the list fetch, minus
  // paging — the download is the whole filtered set.
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (risk) params.set('risk', risk);
    if (search.trim()) params.set('q', search.trim());
    if (archived) params.set('archived', 'true');
    params.set('sort', sortField);
    params.set('order', sortOrder);
    setExporting(true);
    try {
      await apiDownload(`/requests/export.csv?${params}`, 'docket-tprm-requests.csv');
    } catch (err) {
      toast.error('Export failed', err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  // The shared sort props, spread at each header. This used to be a local
  // <SortHeader> wrapper component declared right here in the body — but a
  // component defined inside a render gets a NEW function identity on every
  // render, so React treats it as a different element TYPE and remounts the
  // whole subtree. That destroyed the focused sort button on every sort even
  // with the table kept mounted, which is the other half of doc-230. A plain
  // props object has no identity for the reconciler to trip over.
  const sortProps = { sortField, sortOrder, onToggle: handleSort };

  const labelFor = (opts, value) => opts.find((o) => o.value === value)?.label || value;
  const clearAllFilters = () => { setStatus(''); setRisk(''); setSearch(''); setArchived(false); };
  const activeFilters = [
    normalizeSearchValue(search) && {
      key: 'search',
      label: `Search: ${normalizeSearchValue(search)}`,
      onRemove: () => setSearch(''),
    },
    status && { key: 'status', label: `Status: ${labelFor(STATUS_OPTIONS, status)}`, onRemove: () => setStatus('') },
    risk && { key: 'risk', label: `Risk: ${labelFor(RISK_OPTIONS, risk)}`, onRemove: () => setRisk('') },
    archived && { key: 'archived', label: 'Archived', onRemove: () => setArchived(false) },
  ].filter(Boolean);

  // doc-230: the skeleton is FIRST-LOAD only. Rendering it on every `loading`
  // unmounted the table — sortable headers included — on each refetch, so the
  // sort button a keyboard user had just activated was destroyed and focus fell
  // to <body>; a second Space/Enter then did nothing. A refetch over rows we
  // already have keeps the table mounted and marks it aria-busy instead.
  const firstLoad = loading && requests.length === 0;
  const refreshing = loading && requests.length > 0 && !error;

  return (
    <div className="canvas-inner animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">TPRM</div>
          <h1 className="page-title">Requests</h1>
          <div className="page-subtitle">Vendor onboarding and contract requests</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {/* Filter-aware queue export (doc-116): downloads the WHOLE
              filtered set, not just the visible page. */}
          <button
            type="button"
            className="btn outline"
            onClick={handleExport}
            disabled={exporting || loading || pagination.total === 0}
            title={
              pagination.total === 0
                ? 'Nothing to export — no requests match the current filters'
                : 'Download the filtered list as CSV'
            }
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <Link to="/tprm/new" className="btn primary">
            <Plus className="w-4 h-4" />
            New request
          </Link>
        </div>
      </div>

      {/* Search — own full-width row; the admin "Archived" toggle is a flag,
          so it rides here rather than as a dropdown. Mirrors the Contracts list. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            // What CommandK's bare-`/` shortcut focuses (doc-356). Contracts
            // and Counterparties already carried it; without it here `/` fell
            // through to opening the palette on a page that has its own search.
            data-search-input
            className="filter-search-wide"
            icon={Search}
            placeholder="Search vendor, requester, owner…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/* Named saved views (doc-356) — the same control as the contracts
            list, on its own storage key, so a TPRM view never shows up over
            there. Views here are FILTERS ONLY: sort is not URL-synced on this
            list, and a saved view is a querystring. */}
        <SavedViewsMenu storageKey={SAVED_VIEW_KEYS.tprm} hasFilters={activeFilters.length > 0} />
        {isAdmin && (
          <button
            type="button"
            className={archived ? 'btn sm' : 'btn outline sm'}
            onClick={() => setArchived((v) => !v)}
            style={{ whiteSpace: 'nowrap' }}
            title="Show archived requests (admin)"
          >
            Archived
          </button>
        )}
      </div>

      {/* Filters — left-aligned dropdowns (only two, so not stretched). Risk is
          client-side; status hits the backend.

          Option keys are prefix + value + index (doc-339, same shape as
          ContractsList): both lists lead with an empty-valued "All …"
          placeholder, so keying on the value alone means a second empty-valued
          entry collides with it and React silently drops one. These two lists
          are imported from components/RequestBadges, i.e. edited in a different
          file than the one that would break. */}
      <div className="filter-row natural" style={{ marginBottom: activeFilters.length ? 10 : 16 }}>
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o, i) => (
            <option key={`status-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select className="filter-select" value={risk} onChange={(e) => setRisk(e.target.value)}>
          {RISK_OPTIONS.map((o, i) => (
            <option key={`risk-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearAllFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      {/* Results */}
      {firstLoad ? (
        <SkeletonTable rows={6} />
      ) : error ? (
        <div className="callout red">
          <div className="callout-body">
            <strong>Error loading requests.</strong> {error}
            <div style={{ marginTop: 10 }}>
              <button
                className="btn outline sm"
                onClick={() => setRetryToken((token) => token + 1)}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : requests.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ padding: '64px 24px', textAlign: 'center' }}>
            <div
              style={{
                width: 44, height: 44, borderRadius: 999,
                background: 'var(--surface-alpha-tertiary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12,
              }}
            >
              <ClipboardList className="w-5 h-5" style={{ color: 'var(--icon-tertiary)' }} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
              {status || risk || search || archived ? 'No matching requests' : 'No requests yet'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              {status || risk || search || archived
                ? 'Try clearing the filters.'
                : 'Submit a new vendor contract request to get started.'}
            </div>
            {status || risk || search || archived ? (
              <button className="btn outline sm" onClick={() => { setStatus(''); setRisk(''); setSearch(''); setArchived(false); }}>
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            ) : (
              <Link to="/tprm/new" className="btn primary sm">
                <Plus className="w-3.5 h-3.5" />
                New request
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div
            style={{ overflowX: 'auto' }}
            className={refreshing ? 'list-refreshing' : undefined}
            aria-busy={refreshing || undefined}
          >
          <table className="tbl">
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      ref={headerCheckboxRef}
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Select all requests on this page"
                      style={{ cursor: 'pointer', display: 'block' }}
                    />
                  </th>
                )}
                <SortableHeader {...sortProps} field="vendorName" style={{ width: '26%' }}>Vendor</SortableHeader>
                <SortableHeader {...sortProps} field="status" style={{ width: 130 }}>Status</SortableHeader>
                <SortableHeader {...sortProps} field="recommendedRiskRating" style={{ width: 90 }}>Risk</SortableHeader>
                <SortableHeader {...sortProps} field="legalReviewStatus" style={{ width: 130 }}>Legal</SortableHeader>
                <SortableHeader {...sortProps} field="securityReviewStatus" style={{ width: 130 }}>Security</SortableHeader>
                <SortableHeader {...sortProps} field="lightsparkEntity" style={{ width: 180 }}>Entity</SortableHeader>
                <SortableHeader {...sortProps} field="estimatedFees" style={{ width: 110, textAlign: 'right' }}>
                  <span style={{ marginLeft: 'auto' }}>Value</span>
                </SortableHeader>
                <SortableHeader {...sortProps} field="updatedAt" style={{ width: 110 }}>Updated</SortableHeader>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/tprm/${r.id}`)}
                  onKeyDown={(event) => {
                    // Only when the row itself is focused — Space on the
                    // selection checkbox must toggle it, not navigate.
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/tprm/${r.id}`);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  style={selectable && selectedIds.has(r.id)
                    ? { background: 'var(--surface-alpha-primary)' }
                    : undefined}
                >
                  {selectable && (
                    // Checkbox cell must not trigger the row's navigate.
                    <td onClick={(event) => event.stopPropagation()} style={{ cursor: 'default' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelected(r.id)}
                        aria-label={`Select ${r.vendorName || 'Untitled request'}`}
                        style={{ cursor: 'pointer', display: 'block' }}
                      />
                    </td>
                  )}
                  <td>
                    <div className="row" style={{ gap: 10, minWidth: 0 }}>
                      <VendorMonogram name={r.vendorName} size={28} />
                      <div style={{ minWidth: 0 }}>
                        {/* The base 20px leading makes stacked small text
                            look gappy — tighten both lines so the pair reads
                            as one unit next to the monogram. */}
                        <div
                          className="ellipsis"
                          style={{ fontSize: 13, fontWeight: 450, color: 'var(--text-primary)', lineHeight: '17px' }}
                          title={r.vendorName}
                        >
                          {r.vendorName || 'Untitled request'}
                        </div>
                        <div
                          className="ellipsis"
                          style={{ fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: '15px', marginTop: 2 }}
                          title={r.requesterName || r.requesterEmail}
                        >
                          Requested by {r.requesterName || r.requesterEmail || '—'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <RequestStatusBadge status={r.status} />
                  </td>
                  <td>
                    <RiskRatingBadge rating={r.recommendedRiskRating || r.riskRating} />
                  </td>
                  <td>
                    <ReviewStatusBadge status={r.legalReviewStatus} />
                  </td>
                  <td>
                    <ReviewStatusBadge status={r.securityReviewStatus} />
                  </td>
                  <td>
                    <span
                      className="ellipsis"
                      style={{ fontSize: 12.5, color: 'var(--text-secondary)', display: 'block' }}
                      title={r.lightsparkEntity}
                    >
                      {r.lightsparkEntity || '—'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
                      {r.estimatedFees ? fmtMoney(r.estimatedFees) : '—'}
                    </span>
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {relativeTime(r.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {pagination.total > 0 && (
            <div
              style={{
                padding: '10px 24px',
                borderTop: '0.5px solid var(--border-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="row" style={{ gap: 16 }}>
                <span className="tnum">
                  {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)} of {pagination.total}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  <span>Per page</span>
                  <select
                    className="filter-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    style={{ height: 24, padding: '0 22px 0 8px', fontSize: 11 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  style={{ opacity: page === 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="mono tnum" style={{ minWidth: 56, textAlign: 'center' }}>
                  {page} / {pagination.totalPages}
                </span>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  aria-label="Next page"
                  style={{ opacity: page === pagination.totalPages ? 0.4 : 1 }}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk actions over the selection — admin only; the backend enforces
          the same gate on the bulk endpoints. */}
      {selectable && (
        <RequestsBulkActionsBar
          selectedIds={selectedIds}
          archivedView={archived}
          onClearSelection={clearSelection}
          onApplied={refresh}
        />
      )}
    </div>
  );
}
